// Collection item detail popup. Clicking any item in the collection grid
// opens THIS (instead of instantly opening a sealed dossier, or full-page
// redirecting for an opened certificate). It shows the artifact enlarged +
// its key facts + the actions available for that item:
//   * sealed dossier → hero-style presentation echoing the shop's
//     highlighted-dossier (artifact left, title/description/tiers/creator
//     roster right, via the public drop-detail endpoint). "Open dossier"
//     is THE action; "List on the market" is a quiet text link.
//   * opened certificate → List on the market · View full details
//     (both deliberately quiet — the certificate itself is the star).
//
// The panel animates out from the clicked card: we capture the click point
// (viewport px) and translate/scale the panel from there to screen-center
// with a CSS transition — no animation library needed. Enter-only; close is
// a quick fade. A11y: role=dialog + aria-modal, ESC + backdrop-click close,
// initial focus on the panel, body-scroll lock while open.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Badge,
  Button,
  PatronageCertificate,
  SealedDossier,
  Switch,
  TierBadge,
  formatIssueNumber,
} from "@greenhold/components";

import { DemoContentNotice } from "../../components/DemoContentNotice";
import { certPropsFromShare } from "./certProps";
import { useDropForPurchase } from "../dossier-purchase/useDropForPurchase";
import { usePublishToggle } from "./usePublishToggle";
import { useAccountMe } from "../admin/useAccountMe";
import type { CertificateShare, SealedDossierData } from "@greenhold/components";
import type {
  PublishTargetKind,
  SealedDossierVaultRow,
  VaultItem,
} from "./vaultTypes";

export interface CollectionItemDialogProps {
  item: VaultItem;
  /** Click point in viewport px — the panel grows out from here. */
  origin: { x: number; y: number } | null;
  onClose: () => void;
  /** Fires when the user opts to open a sealed dossier (triggers the
   *  reveal ceremony at the page level). */
  onOpenDossier: (row: SealedDossierVaultRow) => void;
  /** Opens the "List for sale" flow for the item (page-level dialogs). */
  onListSealed: (row: SealedDossierVaultRow) => void;
  onListCertificate: (item: Extract<VaultItem, { kind: "opened" }>) => void;
  /** Opens the "Edit listing" flow for an item that is already listed
   *  (page-level dialog; the item carries `active_listing`). */
  onEditListing: (item: VaultItem) => void;
}

function sealedDataFromRow(row: SealedDossierVaultRow): SealedDossierData {
  return {
    id: row.id,
    drop_id: row.drop_id,
    edition_number: row.edition_number,
    portfolio_title: row.portfolio_title,
    drop_status: row.drop_status,
    retiring_at: row.retiring_at,
    seal_color: row.seal_color,
  };
}

function statusVariant(
  status: SealedDossierVaultRow["drop_status"],
): "success" | "warning" | "secondary" {
  if (status === "live") return "success";
  if (status === "retiring") return "warning";
  return "secondary";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function CollectionItemDialog({
  item,
  origin,
  onClose,
  onOpenDossier,
  onListSealed,
  onListCertificate,
  onEditListing,
}: CollectionItemDialogProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const [entered, setEntered] = useState(false);
  // Certificate "loupe" — clicking the cert enlarges it to near-fullscreen
  // so the collector can inspect it. Layered above this popup.
  const [zoomed, setZoomed] = useState(false);

  // Trigger the enter transition after first paint; lock body scroll +
  // restore focus on unmount.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const prevFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      // ESC closes the loupe first (if open), then the popup.
      if (zoomed) {
        setZoomed(false);
        return;
      }
      onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, zoomed]);

  // Initial transform: place the panel at the click point, scaled down.
  // Falls back to a plain scale-up when we have no origin.
  const initialTransform = origin
    ? `translate(${origin.x - window.innerWidth / 2}px, ${
        origin.y - window.innerHeight / 2
      }px) scale(0.35)`
    : "scale(0.9)";

  const titleId = "collection-item-dialog-title";

  return (
    <>
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(9, 20, 15, 0.72)",
        opacity: entered ? 1 : 0,
        transition: "opacity 200ms ease-out",
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`relative max-h-[90vh] w-full overflow-y-auto rounded-xl border bg-brand-forest-800 p-6 text-brand-paper-100 shadow-2xl outline-none ${
          item.kind === "sealed"
            ? "max-w-[720px] border-brand-gold-500/40"
            : "max-w-md border-brand-forest-700"
        }`}
        style={{
          transform: entered ? "translate(0,0) scale(1)" : initialTransform,
          opacity: entered ? 1 : 0,
          transition:
            "transform 260ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease-out",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-lg opacity-70 hover:bg-brand-forest-700 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
        >
          ✕
        </button>

        {item.kind === "sealed" ? (
          <SealedContent
            row={item.sealed_dossier}
            hasActiveListing={item.has_active_listing === true}
            titleId={titleId}
            onOpen={() => {
              onClose();
              onOpenDossier(item.sealed_dossier);
            }}
            onList={() => {
              onClose();
              onListSealed(item.sealed_dossier);
            }}
            onEdit={
              item.active_listing
                ? () => {
                    onClose();
                    onEditListing(item);
                  }
                : undefined
            }
          />
        ) : (
          <CertificateContent
            item={item}
            titleId={titleId}
            onZoom={() => setZoomed(true)}
            onList={() => {
              onClose();
              onListCertificate(item);
            }}
            onEdit={
              item.active_listing
                ? () => {
                    onClose();
                    onEditListing(item);
                  }
                : undefined
            }
            onViewDetails={() => {
              navigate(`/vault/certificates/${item.share.share_id}`);
            }}
          />
        )}
      </div>
    </div>
    {zoomed && item.kind === "opened" && (
      <CertificateLightbox
        share={item.share}
        onClose={() => setZoomed(false)}
      />
    )}
    </>
  );
}

// Story 10.2 — the opt-in publish control, shared by both artifact kinds.
// Copy states plainly that a published item is visible to anyone with the
// link, including signed-out visitors. Not labelled "Highlight".
function PublishRow({
  targetKind,
  targetId,
  checked,
}: {
  targetKind: PublishTargetKind;
  targetId: string;
  checked: boolean;
}) {
  const mutation = usePublishToggle({ targetKind, targetId });
  const accountMe = useAccountMe();
  const profilePrivate =
    accountMe.data?.data.profile_visibility === "private";
  const switchId = `publish-${targetKind}-${targetId}`;
  // The `checked` prop is read from the frozen VaultItem snapshot captured
  // when the dialog opened — it never updates after the mutation, so the
  // Switch would sit stuck on its old position. Track the state locally and
  // flip it optimistically (reverting on error); resync if the dialog is
  // re-pointed at a different item.
  const [isOn, setIsOn] = useState(checked);
  useEffect(() => {
    setIsOn(checked);
  }, [checked, targetId]);
  function handleChange(next: boolean) {
    setIsOn(next);
    mutation.mutate(
      { state: next },
      {
        onError: (error) => {
          setIsOn(!next);
          toast.error(error.message);
        },
      },
    );
  }
  return (
    <div className="grid gap-1.5 rounded-md border border-brand-forest-700 bg-brand-forest-900/40 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={switchId}
          className="cursor-pointer font-body text-xs font-semibold"
        >
          Show in public collection
        </label>
        <Switch
          id={switchId}
          checked={isOn}
          disabled={mutation.isPending}
          onCheckedChange={handleChange}
        />
      </div>
      <p className="m-0 text-[11px] opacity-60">
        Visible to anyone with the link, including people who are not signed
        in.
      </p>
      {isOn && profilePrivate && (
        <p className="m-0 text-[11px] text-brand-gold-300">
          Your profile is private, so your collection page returns 404 — this
          item still opens via its own link.{" "}
          <Link to="/account/profile" className="underline">
            Make it public
          </Link>
          .
        </p>
      )}
    </div>
  );
}

// Tier row mirrors the shop hero's static "Tiers inside" strip.
const SEALED_TIERS = ["common", "preferred", "premium", "founders"] as const;

function SealedContent({
  row,
  hasActiveListing,
  titleId,
  onOpen,
  onList,
  onEdit,
}: {
  row: SealedDossierVaultRow;
  hasActiveListing: boolean;
  titleId: string;
  onOpen: () => void;
  onList: () => void;
  onEdit?: () => void;
}) {
  const retired = row.drop_status === "retired";
  // The vault row carries no roster/description — fetch the drop's public
  // detail (same endpoint the shop hero uses) for portraits + copy.
  const detail = useDropForPurchase(row.drop_id);
  const creators = detail.data?.creators ?? [];

  return (
    <div className="flex flex-wrap items-center gap-8">
      <div className="w-[200px] shrink-0 self-center">
        <SealedDossier
          dossier={sealedDataFromRow(row)}
          size="md"
          state={retired ? "retired" : "selected"}
          game={row.portfolio_theme ?? undefined}
          showRetirementWarning={row.drop_status === "retiring"}
        />
      </div>

      <div className="grid min-w-[260px] flex-1 gap-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-brand-gold-300">
            Sealed dossier · Issue #{formatIssueNumber(row.edition_number)}
          </span>
          <Badge variant={statusVariant(row.drop_status)}>
            {row.drop_status}
          </Badge>
        </div>

        <h2 id={titleId} className="m-0 font-heading text-2xl">
          {row.portfolio_title}
        </h2>

        {detail.data?.portfolio_description ? (
          <p className="m-0 whitespace-pre-line font-body text-[0.88rem] text-brand-paper-100/75">
            {detail.data.portfolio_description}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-paper-100/60">
            Tiers inside
          </span>
          {SEALED_TIERS.map((t) => (
            <TierBadge key={t} tier={t} size="sm" />
          ))}
        </div>

        {detail.isLoading ? (
          <p className="m-0 text-xs opacity-50">Loading creator roster…</p>
        ) : creators.length > 0 ? (
          <div className="grid gap-2">
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-paper-100/60">
              Creators inside · {detail.data?.creator_count ?? creators.length}
            </span>
            <div className="max-h-44 overflow-y-auto pr-1">
              <div className="flex flex-wrap gap-x-5 gap-y-3">
                {creators.map((c) => {
                  const name =
                    c.display_name || (c.handle ? `@${c.handle}` : "?");
                  return (
                    <div
                      key={c.user_id}
                      className="grid w-16 justify-items-center gap-1.5"
                    >
                      {c.image_signed_url ? (
                        <img
                          src={c.image_signed_url}
                          alt=""
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-gold-500/20 font-heading text-base text-brand-gold-300">
                          {name.replace(/^@/, "").charAt(0).toUpperCase() || "?"}
                        </span>
                      )}
                      <span className="max-w-full truncate text-center font-mono text-[0.58rem] uppercase tracking-[0.08em] text-brand-paper-100/60">
                        {name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        <p className="m-0 text-xs opacity-60">
          Acquired {formatDate(row.acquired_at)}
        </p>

        <div className="mt-1.5 grid gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="primary"
              size="lg"
              onClick={onOpen}
              disabled={retired}
            >
              Open dossier
            </Button>
            <span className="text-xs opacity-60">
              Once opened, a dossier can't be re-sealed.
            </span>
          </div>
          {hasActiveListing ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-brand-forest-700 bg-brand-forest-900/40 px-3 py-2">
              <p className="m-0 text-xs opacity-80">
                This dossier is currently listed on the market.
              </p>
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded text-xs font-semibold text-brand-gold-300 underline-offset-2 transition-colors hover:text-brand-gold-200 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
                >
                  Edit listing
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={onList}
              className="justify-self-start rounded text-xs text-brand-paper-100/60 underline-offset-2 transition-colors hover:text-brand-paper-100 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
            >
              List on the market
            </button>
          )}
          <PublishRow
            targetKind="sealed_dossier"
            targetId={row.id}
            checked={row.is_public_in_collection}
          />
        </div>
      </div>
    </div>
  );
}

function CertificateContent({
  item,
  titleId,
  onZoom,
  onList,
  onEdit,
  onViewDetails,
}: {
  item: Extract<VaultItem, { kind: "opened" }>;
  titleId: string;
  onZoom: () => void;
  onList: () => void;
  onEdit?: () => void;
  onViewDetails: () => void;
}) {
  const { share } = item;
  const hasActiveListing = item.has_active_listing === true;
  return (
    <div className="flex flex-col items-center gap-4">
      {/* Click the cert to inspect it at near-fullscreen. */}
      <button
        type="button"
        onClick={onZoom}
        aria-label="Enlarge certificate"
        className="group relative cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-forest-800"
      >
        <PatronageCertificate width={240} {...certPropsFromShare(share, { withQuote: true })} />
        <span className="pointer-events-none mt-1 block text-center text-[11px] uppercase tracking-wider opacity-50 transition-opacity group-hover:opacity-80">
          Click to enlarge
        </span>
      </button>

      <div className="w-full space-y-1 text-center">
        <h2 id={titleId} className="font-heading text-xl font-bold">
          {share.snapshot_display_name}
        </h2>
        <p className="text-sm opacity-80">
          {share.tier} · Certificate #{share.share_serial}
        </p>
        <p className="text-xs opacity-60">
          From {share.portfolio_title} · Opened {formatDate(share.opened_at)}
        </p>
      </div>

      {/* Both actions are deliberately quiet text links: the certificate
          itself is the star of this popup. (Plain ghost Buttons render
          near-invisible on the dark panel — dark text on dark fill — so
          these are hand-styled light-text links instead.) */}
      <div className="flex w-full flex-col items-center gap-1.5">
        {hasActiveListing ? (
          <div className="flex flex-col items-center gap-1 rounded-md border border-brand-forest-700 bg-brand-forest-900/40 px-3 py-2 text-center">
            <p className="m-0 text-xs opacity-80">
              This certificate is currently listed on the market.
            </p>
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="rounded text-xs font-semibold text-brand-gold-300 underline-offset-2 transition-colors hover:text-brand-gold-200 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
              >
                Edit listing
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onList}
            className="rounded text-xs text-brand-paper-100/60 underline-offset-2 transition-colors hover:text-brand-paper-100 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
          >
            List on the market
          </button>
        )}
        <button
          type="button"
          onClick={onViewDetails}
          className="rounded text-[11px] text-brand-paper-100/40 underline-offset-2 transition-colors hover:text-brand-paper-100/80 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
        >
          View full details
        </button>
      </div>

      <div className="w-full">
        <PublishRow
          targetKind="share"
          targetId={share.share_id}
          checked={share.is_public_in_collection ?? false}
        />
      </div>

      <DemoContentNotice />
    </div>
  );
}

/// Near-fullscreen certificate "loupe". Sizes the PatronageCertificate
/// (320×452 intrinsic) to the larger fit against the viewport so the
/// collector can inspect the wear, signature + fields up close. Backdrop
/// click closes (ESC is handled by the parent dialog, which layers this).
function CertificateLightbox({
  share,
  onClose,
}: {
  share: CertificateShare;
  onClose: () => void;
}) {
  const [width, setWidth] = useState(() => fitCertWidth());
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    function onResize() {
      setWidth(fitCertWidth());
    }
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Certificate for ${share.snapshot_display_name}, enlarged`}
      className="fixed inset-0 z-[60]"
      style={{
        background: "rgba(6, 15, 11, 0.92)",
        opacity: entered ? 1 : 0,
        transition: "opacity 180ms ease-out",
      }}
    >
      {/* Full-screen backdrop click-catcher BEHIND everything — closes on any
          click that reaches it. */}
      <div className="absolute inset-0" aria-hidden="true" onClick={onClose} />
      {/* Centering layer is pointer-events:none, so clicks pass THROUGH it to
          the backdrop everywhere except on the cert itself (which re-enables
          pointer events + stops propagation). Flex centres the cert correctly
          at any width (no abspos shrink-to-fit clamping). */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className="pointer-events-auto"
          style={{
            transform: entered ? "scale(1)" : "scale(0.96)",
            opacity: entered ? 1 : 0,
            transition:
              "transform 220ms cubic-bezier(0.16, 1, 0.3, 1), opacity 180ms ease-out",
          }}
        >
          <PatronageCertificate
            width={width}
            {...certPropsFromShare(share, { withQuote: true })}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close enlarged certificate"
        className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-xl text-brand-paper-100 opacity-80 hover:bg-white/10 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
      >
        ✕
      </button>
    </div>
  );
}

/// Largest PatronageCertificate width that fits ~92% of the viewport in
/// both axes (the artifact is 320×452, aspect 320/452).
function fitCertWidth(): number {
  if (typeof window === "undefined") return 320;
  const byWidth = window.innerWidth * 0.92;
  const byHeight = window.innerHeight * 0.92 * (320 / 452);
  return Math.max(200, Math.min(byWidth, byHeight));
}
